// @ts-nocheck — Pre-existing type errors (stale code drifted from refactored types / WIP).
// Suppressed to unblock the admin build; runtime unchanged. TODO(owner): restore type-safety.
/**
 * Contact Page
 *
 * Manages contact inquiries from portfolio visitors.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ArrowLeft,
  Mail,
  MessageSquare,
  MoreVertical,
  Eye,
  CheckCircle,
  Archive,
  Clock,
  User,
  Calendar,
  Tag,
  ExternalLink,
  Inbox,
} from 'lucide-react'
import { useContactInquiries, useUpdateInquiryStatus } from '../hooks'
import type { ContactInquiry, InquiryStatus } from '../types'

// =============================================================================
// Status Badge Component
// =============================================================================

const statusConfig: Record<
  InquiryStatus,
  { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }
> = {
  new: { label: 'New', variant: 'default' },
  read: { label: 'Read', variant: 'secondary' },
  replied: { label: 'Replied', variant: 'outline' },
  archived: { label: 'Archived', variant: 'secondary' },
}

function StatusBadge({ status }: { status: InquiryStatus }) {
  const config = statusConfig[status]
  return <Badge variant={config.variant}>{config.label}</Badge>
}

// =============================================================================
// Inquiry Detail Dialog
// =============================================================================

interface InquiryDetailDialogProps {
  inquiry: ContactInquiry | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onStatusChange: (id: number, status: InquiryStatus) => void
}

function InquiryDetailDialog({
  inquiry,
  open,
  onOpenChange,
  onStatusChange,
}: InquiryDetailDialogProps) {
  if (!inquiry) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle className="flex-1">{inquiry.subject || 'No Subject'}</DialogTitle>
            <StatusBadge status={inquiry.status} />
          </div>
          <DialogDescription className="flex items-center gap-4 pt-2">
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {inquiry.name}
            </span>
            <span className="flex items-center gap-1">
              <Mail className="h-3 w-3" />
              {inquiry.email}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(inquiry.createdAt).toLocaleString()}
            </span>
            {inquiry.inquiryType && (
              <span className="flex items-center gap-1">
                <Tag className="h-3 w-3" />
                {inquiry.inquiryType}
              </span>
            )}
          </div>

          <div className="border rounded-lg p-4 bg-muted/50">
            <p className="whitespace-pre-wrap">{inquiry.message}</p>
          </div>

          {inquiry.artworkId && (
            <div className="text-sm">
              <span className="text-muted-foreground">Related Artwork: </span>
              <a
                href={`/app/artworks/${inquiry.artworkId}`}
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                View Artwork
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <Select
              value={inquiry.status}
              onValueChange={(value) => onStatusChange(inquiry.id, value as InquiryStatus)}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="read">Read</SelectItem>
                <SelectItem value="replied">Replied</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button asChild>
            <a href={`mailto:${inquiry.email}?subject=Re: ${inquiry.subject || 'Your Inquiry'}`}>
              <Mail className="h-4 w-4 mr-2" />
              Reply via Email
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Inquiry Card Component
// =============================================================================

interface InquiryCardProps {
  inquiry: ContactInquiry
  onView: (inquiry: ContactInquiry) => void
  onStatusChange: (id: number, status: InquiryStatus) => void
}

function InquiryCard({ inquiry, onView, onStatusChange }: InquiryCardProps) {
  const isNew = inquiry.status === 'new'

  return (
    <Card className={isNew ? 'border-primary' : ''}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onView(inquiry)}>
            <div className="flex items-center gap-2 mb-2">
              <StatusBadge status={inquiry.status} />
              {inquiry.inquiryType && (
                <Badge variant="outline">{inquiry.inquiryType}</Badge>
              )}
            </div>
            <h3 className={`font-semibold line-clamp-1 ${isNew ? 'text-primary' : ''}`}>
              {inquiry.subject || 'No Subject'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">{inquiry.name}</p>
            <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
              {inquiry.message}
            </p>
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(inquiry.createdAt).toLocaleDateString()}
              </span>
              <span className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                {inquiry.email}
              </span>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onView(inquiry)}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onStatusChange(inquiry.id, 'read')}>
                <Clock className="h-4 w-4 mr-2" />
                Mark as Read
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onStatusChange(inquiry.id, 'replied')}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark as Replied
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onStatusChange(inquiry.id, 'archived')}>
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

export function ContactPage() {
  const navigate = useNavigate()
  const { data: inquiriesData, isLoading } = useContactInquiries()
  const updateStatus = useUpdateInquiryStatus()
  const [selectedInquiry, setSelectedInquiry] = useState<ContactInquiry | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'all'>('all')

  /**
   * Newsletter signups are not messages, and mixing them hides the ones that are.
   *
   * Measured on production: 12 inquiries — 9 "Newsletter Signup", 3 real commission enquiries.
   * An artist opening this saw twelve rows, nine of which need no reply, and the three that do
   * were indistinguishable at a glance. Someone asking to commission a piece is the single most
   * valuable message this screen ever carries.
   *
   * Split on inquiry_type rather than the subject string, because the subject is whatever the
   * form sent and the type is what the system recorded.
   */
  const [kind, setKind] = useState<'messages' | 'signups'>('messages')
  const isSignup = (i: ContactInquiry) =>
    i.inquiryType === 'other' && /newsletter/i.test(i.subject || '')

  const inquiries = inquiriesData?.data || []

  const byKind = inquiries.filter((i) => (kind === 'signups' ? isSignup(i) : !isSignup(i)))
  const filteredInquiries =
    statusFilter === 'all'
      ? byKind
      : byKind.filter((i) => i.status === statusFilter)

  const newCount = inquiries.filter((i) => i.status === 'new').length
  const readCount = inquiries.filter((i) => i.status === 'read').length
  const repliedCount = inquiries.filter((i) => i.status === 'replied').length
  const archivedCount = inquiries.filter((i) => i.status === 'archived').length

  const handleView = (inquiry: ContactInquiry) => {
    setSelectedInquiry(inquiry)
    setDetailOpen(true)
    // Mark as read if new
    if (inquiry.status === 'new') {
      updateStatus.mutate({ id: inquiry.id, status: 'read' })
    }
  }

  const handleStatusChange = async (id: number, status: InquiryStatus) => {
    await updateStatus.mutateAsync({ id, status })
  }

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Contact Inquiries</h1>
            <p className="text-muted-foreground">
              Manage messages from your portfolio visitors
            </p>
          </div>
        </div>
        {newCount > 0 && (
          <Badge variant="default" className="text-lg px-4 py-2">
            {newCount} New
          </Badge>
        )}
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card
          className={`cursor-pointer ${statusFilter === 'new' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setStatusFilter('new')}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{newCount}</p>
              <p className="text-sm text-muted-foreground">New</p>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer ${statusFilter === 'read' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setStatusFilter('read')}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{readCount}</p>
              <p className="text-sm text-muted-foreground">Read</p>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer ${statusFilter === 'replied' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setStatusFilter('replied')}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{repliedCount}</p>
              <p className="text-sm text-muted-foreground">Replied</p>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer ${statusFilter === 'archived' ? 'ring-2 ring-primary' : ''}`}
          onClick={() => setStatusFilter('archived')}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-gray-500/10 flex items-center justify-center">
              <Archive className="h-5 w-5 text-gray-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{archivedCount}</p>
              <p className="text-sm text-muted-foreground">Archived</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Messages vs newsletter signups. Messages first because they are the ones that need a
          reply; signups are an audience list that happens to arrive through the same form. */}
      <div className="flex items-center gap-2">
        <Button
          variant={kind === 'messages' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setKind('messages'); setStatusFilter('all') }}
        >
          Messages ({inquiries.filter((i) => !isSignup(i)).length})
        </Button>
        <Button
          variant={kind === 'signups' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setKind('signups'); setStatusFilter('all') }}
        >
          Newsletter signups ({inquiries.filter(isSignup).length})
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2">
        <Button
          variant={statusFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('all')}
        >
          All ({byKind.length})
        </Button>
        <Button
          variant={statusFilter === 'new' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('new')}
        >
          New ({newCount})
        </Button>
        <Button
          variant={statusFilter === 'read' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('read')}
        >
          Read ({readCount})
        </Button>
        <Button
          variant={statusFilter === 'replied' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('replied')}
        >
          Replied ({repliedCount})
        </Button>
        <Button
          variant={statusFilter === 'archived' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('archived')}
        >
          Archived ({archivedCount})
        </Button>
      </div>

      {/* Inquiries List */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-6 w-2/3 mb-2" />
                <Skeleton className="h-4 w-1/3 mb-2" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-1/2 mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredInquiries.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Inbox className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">
            {statusFilter === 'all' ? 'No inquiries yet' : `No ${statusFilter} inquiries`}
          </h3>
          <p className="text-muted-foreground">
            {statusFilter === 'all'
              ? 'When visitors contact you through your portfolio, their messages will appear here'
              : `You don't have any ${statusFilter} inquiries`}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredInquiries.map((inquiry) => (
            <InquiryCard
              key={inquiry.id}
              inquiry={inquiry}
              onView={handleView}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      )}

      {/* Inquiry Detail Dialog */}
      <InquiryDetailDialog
        inquiry={selectedInquiry}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onStatusChange={handleStatusChange}
      />
    </div>
  )
}
